import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useVerifySalesforceIntegration from 'hooks/services/salesforce/useVerifySalesforceIntegration';
import PATH from 'crono-fe-common/constants/path';
import { useAuth } from 'context/auth';
import useVerifyHubspotIntegration from 'hooks/services/hubspot/useVerifyHubspotIntegration';
import useVerifyPipedriveIntegration from 'hooks/services/pipedrive/useVerifyPipedriveIntegration';
import useVerifyAttioIntegration from 'hooks/services/attio/useVerifyAttioIntegration';
import useVerifyZohoIntegration from 'hooks/services/zoho/useVerifyZohoIntegration';
import useVerifyDynamicsIntegration from 'hooks/services/dynamics/useVerifyDynamicsIntegration';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { invalidateSyncCrmPipelines } from '../services/pipeline/useSyncCrmPipelines';
import { useQueryClient } from '@tanstack/react-query';
import useVerifyChartMogulIntegration from '../services/chartMogul/useVerifyChartMogulIntegration';

const useCRMIntegrationGuard = (
  providedIntegrationType: IntegrationType | null = null,
) => {
  const { user, onboardingState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoadingCronoIntegration, setIsLoadingCronoIntegration] =
    useState(true);

  const {
    isLoading: isLoadingSF,
    isError: isErrorSF,
    isSuccess: isSuccessSF,
    verifySalesforceIntegration,
  } = useVerifySalesforceIntegration();

  const {
    isLoading: isLoadingHS,
    isError: isErrorHS,
    isSuccess: isSuccessHS,
    verifyHubspotIntegration,
  } = useVerifyHubspotIntegration();

  const {
    isLoading: isLoadingPD,
    isError: isErrorPD,
    isSuccess: isSuccessPD,
    verifyPipedriveIntegration,
  } = useVerifyPipedriveIntegration();

  const {
    isLoading: isLoadingCM,
    isError: isErrorCM,
    isSuccess: isSuccessCM,
    data: chartMogulData,
    verifyChartMogulIntegration,
  } = useVerifyChartMogulIntegration();

  const {
    isLoading: isLoadingAT,
    isError: isErrorAT,
    isSuccess: isSuccessAT,
    verifyAttioIntegration,
  } = useVerifyAttioIntegration();

  const {
    isLoading: isLoadingZH,
    isError: isErrorZH,
    isSuccess: isSuccessZH,
    verifyZohoIntegration,
  } = useVerifyZohoIntegration();

  const {
    isLoading: isLoadingDY,
    isError: isErrorDY,
    isSuccess: isSuccessDY,
    data: dynamicsData,
    verifyDynamicsIntegration,
  } = useVerifyDynamicsIntegration();

  const queryClient = useQueryClient();
  useEffect(() => {
    if (
      isSuccessHS ||
      isSuccessPD ||
      isSuccessSF ||
      isSuccessCM ||
      isSuccessAT ||
      isSuccessZH ||
      isSuccessDY
    ) {
      invalidateSyncCrmPipelines(queryClient);
    }
  }, [
    isSuccessHS,
    isSuccessPD,
    isSuccessSF,
    isSuccessCM,
    isSuccessAT,
    isSuccessZH,
    isSuccessDY,
  ]);

  useEffect(() => {
    if (
      isErrorSF ||
      isErrorHS ||
      isErrorPD ||
      isErrorCM ||
      isErrorAT ||
      isErrorZH ||
      isErrorDY
    ) {
      if (onboardingState === undefined) return;
      const targetPath =
        onboardingState === null ? PATH.CONNECT : PATH.ONBOARDING;
      if (location.pathname !== targetPath) {
        navigate(targetPath);
      }
    }
  }, [
    isErrorSF,
    isErrorHS,
    isErrorPD,
    isErrorCM,
    isErrorAT,
    isErrorZH,
    isErrorDY,
    onboardingState,
    navigate,
    location.pathname,
  ]);

  const refetch = useCallback(() => {
    const integrationType =
      providedIntegrationType ?? user?.company?.integrationType;
    if (integrationType === IntegrationType.SALESFORCE) {
      verifySalesforceIntegration();
    } else if (integrationType === IntegrationType.HUBSPOT) {
      verifyHubspotIntegration();
    } else if (integrationType === IntegrationType.PIPEDRIVE) {
      verifyPipedriveIntegration();
    } else if (integrationType === IntegrationType.CHART_MOGUL) {
      verifyChartMogulIntegration();
    } else if (integrationType === IntegrationType.ATTIO) {
      verifyAttioIntegration();
    } else if (integrationType === IntegrationType.ZOHO) {
      verifyZohoIntegration();
    } else if (integrationType === IntegrationType.DYNAMICS) {
      verifyDynamicsIntegration();
    }
  }, [
    providedIntegrationType,
    user,
    verifySalesforceIntegration,
    verifyHubspotIntegration,
    verifyPipedriveIntegration,
    verifyChartMogulIntegration,
    verifyAttioIntegration,
    verifyZohoIntegration,
    verifyDynamicsIntegration,
  ]);

  useEffect(() => {
    if (!user) return;
    refetch();
    setIsLoadingCronoIntegration(false);
  }, [user, refetch]);

  return {
    isSuccess:
      (isSuccessHS ||
        isSuccessSF ||
        isSuccessCM ||
        isSuccessPD ||
        isSuccessAT ||
        isSuccessZH ||
        isSuccessDY) ??
      false,
    isLoading:
      isLoadingSF ||
      isLoadingHS ||
      isLoadingCronoIntegration ||
      isLoadingPD ||
      isLoadingCM ||
      isLoadingAT ||
      isLoadingZH ||
      isLoadingDY,
    isError:
      (isErrorSF ||
        isErrorHS ||
        isErrorPD ||
        isErrorCM ||
        isErrorAT ||
        isErrorZH ||
        isErrorDY) ??
      false,
    isLoadingHS,
    isErrorHS,
    isSuccessHS,
    isLoadingSF,
    isErrorSF,
    isSuccessSF,
    isLoadingPD,
    isSuccessPD,
    isErrorPD,
    isLoadingCM,
    isSuccessCM,
    isErrorCM,
    isLoadingAT,
    isSuccessAT,
    isErrorAT,
    isLoadingZH,
    isSuccessZH,
    isErrorZH,
    isLoadingDY,
    isSuccessDY,
    isErrorDY,
    dynamicsData,
    chartMogulData,
    refetch,
  };
};

export default useCRMIntegrationGuard;
